import { User, UserRole } from "@campaign/shared-types";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getMe } from "../services/api";
import { AmplifyAuthService } from "../services/auth/AmplifyAuthService";
import { IAuthService } from "../services/auth/IAuthService";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isPending: boolean;
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create a singleton instance to avoid recreating on every render
const defaultAuthService = new AmplifyAuthService();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
  authService?: IAuthService;
}

export function AuthProvider({
  children,
  authService = defaultAuthService,
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPending, setIsPending] = useState(false);

  const checkUser = useCallback(async () => {
    console.log("Checking user authentication status...");
    try {
      const currentUser = await authService.getCurrentUser();
      console.log("✓ Current user found:", currentUser);

      if (!currentUser) {
        console.log("✗ No authenticated user");
        setUser(null);
        setIsPending(false);
        setLoading(false);
        return;
      }

      // Get basic identity info from Cognito attributes
      let email = "";
      let firstName = "";
      let lastName = "";
      try {
        const attributes = await authService.fetchUserAttributes();
        console.log("✓ User attributes:", attributes);
        email = attributes.email || currentUser.username;
        firstName = attributes.given_name || "";
        lastName = attributes.family_name || "";
      } catch (attrError: any) {
        console.log(
          "Note: Could not fetch attributes via Identity Pool, using User Pool directly"
        );
        email = currentUser.username.includes("@")
          ? currentUser.username
          : `${currentUser.username}@example.com`;
      }

      // Get role, assignments, and approval status from DynamoDB via /me API
      // This is the source of truth — Cognito JWT does not store role/assignments
      try {
        const meData = await getMe();
        console.log("✓ /me data:", meData);
        setUser({
          email: meData.email || email,
          firstName: meData.firstName || firstName,
          lastName: meData.lastName || lastName,
          role: meData.role,
          approved: meData.approved,
          assignments: meData.assignments,
        });
        setIsPending(!meData.approved);
      } catch (meError: any) {
        console.warn(
          "Could not fetch /me, falling back to Cognito attributes:",
          meError.message
        );
        // Fallback: unapproved users may not have a /me record yet
        setUser({
          email,
          firstName,
          lastName,
          role: "readonly" as UserRole,
          approved: false,
        });
        setIsPending(true);
      }
    } catch (error: any) {
      console.log("✗ No authenticated user:", error.message || error);
      setUser(null);
      setIsPending(false);
    } finally {
      setLoading(false);
    }
  }, [authService]);

  useEffect(() => {
    console.log("AuthProvider mounted, checking user...");

    // Capture any OAuth errors from URL before they disappear
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");
    if (error) {
      console.error("🚨 OAuth Error in URL:");
      console.error("Error:", error);
      console.error("Description:", errorDescription);
      alert(`OAuth Error: ${error}\n\n${errorDescription}`);
    }

    checkUser();

    // Listen for auth state changes
    const unsubscribe = authService.listenToAuthEvents((payload) => {
      console.log("Auth event:", payload);

      switch (payload.event) {
        case "signInWithRedirect":
          console.log("Sign in with redirect event");
          checkUser();
          break;
        case "signInWithRedirect_failure":
          console.error("Sign in with redirect failed:", payload.data);
          setLoading(false);
          break;
        case "signedIn":
          console.log("User signed in");
          checkUser();
          break;
        case "signedOut":
          console.log("User signed out");
          setUser(null);
          setIsPending(false);
          setLoading(false);
          break;
        case "tokenRefresh":
          console.log("Token refresh");
          checkUser();
          break;
        case "tokenRefresh_failure":
          console.error("Token refresh failed");
          break;
      }
    });

    return unsubscribe;
  }, [authService, checkUser]);

  const login = () => {
    console.log("Initiating sign in with Google redirect...");
    authService.signInWithRedirect("Google");
  };

  const logout = async () => {
    try {
      console.log("Signing out...");
      await authService.signOut();
      setUser(null);
      setIsPending(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const refreshUser = async () => {
    try {
      console.log("Forcing token refresh and user attributes update...");
      await authService.refreshUserAttributes();
      console.log("✓ Tokens refreshed, re-checking user...");
      await checkUser();
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && !isPending,
        isPending,
        login,
        logout,
        refreshUser,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
